<template>
  <div>
    <CollectionView searchable :endpoint="utilities.path('communities/@/modules/applications/categories', communityId)" v-model:reload="reload">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h4 class="mb-0">{{ translate("categories") }}</h4>
          <ButtonView type="normal" color="primary" @click="creating = true">
            {{ translate("create") }}
          </ButtonView>
        </div>
      </template>
      <template v-slot:content="{ items }">
        <div class="card">
          <div class="table-responsive">
            <div class="table">
              <div class="table-header">
                <div class="table-header-cell">{{ translate("name") }}</div>
                <div class="table-header-cell">{{ translate("color") }}</div>
                <div class="table-header-cell">{{ translate("actions") }}</div>
              </div>
              <div class="table-body">
                <RouterLink class="table-row" v-for="item in items" :key="item.id" :to="{ name: 'community.modules.applications.category', params: { categoryId: item.id }}">
                  <div class="table-column">
                    {{ item.name }}
                  </div>
                  <div class="table-column">
                    <i class="fas fa-circle fa-fw" :style="{ color: item.color }"></i>
                  </div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <ButtonView v-if="canModify" type="soft" color="primary" size="small" @click.prevent="editing = item">
                        <i class="fas fa-edit"></i>
                      </ButtonView>
                      <ResourcePermissionModalView type="modules.applications.category" :id="item.id"/>
                      <TargetedModalHttpFormView v-if="canModify" size="small" :endpoint="utilities.path('modules/applications/categories/@', item.id)" method="delete" @success="reload = true">
                        <template v-slot:target="{ show }">
                          <ButtonView type="soft" color="danger" size="small" @click.prevent="show">
                            <i class="fas fa-trash"></i>
                          </ButtonView>
                        </template>
                        <template v-slot:form="{ submit, loading, hide }">
                          <h2 class="mb-4">{{ item.name }}</h2>
                          <SpinnerView :active="loading" color="danger"/>
                          <div class="d-flex align-items-center g-4">
                            <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                              {{ translate("delete") }}
                            </ButtonView>
                            <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                              {{ translate("cancel") }}
                            </ButtonView>
                          </div>
                        </template>
                      </TargetedModalHttpFormView>
                    </div>
                  </div>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CollectionView>
    <ModalHttpFormView v-if="canModify" :endpoint="creating ? utilities.path('communities/@/modules/applications/categories', communityId) : utilities.path('modules/applications/categories/@', editing?.id)" :method="creating ? 'post' : 'put'" :content="creating ? { open: true } : editing" :active="creating || editing !== undefined" @update:active="creating = false; editing = undefined" @success="reload = true">
      <template v-slot:form="{ content, errors, submit, loading }">
        <div class="d-flex flex-column g-4">
          <InputView type="text" :min="1" :max="32" v-model:value="content.name" :title="translate('name')" :errors="errors.name" required/>
          <InputView type="color" v-model:value="content.color" :placeholder="translate('color')" :title="translate('color')" :errors="errors.color" required/>
          <InputView type="textarea" :min="1" :max="300" v-model:value="content.description" :title="translate('description')" :errors="errors.description"/>
          <HttpSelectInputView :endpoint="utilities.path('communities/@/modules/applications/conditions', communityId)" :title="translate('conditions')" multiple v-model:value="content.conditions">
            <template v-slot:option="{ option, preview }">
              <span :class="{ 'bg-secondary px-2 py-1 me-1 mt-1 rounded-3': preview }">{{ option.title }}</span>
            </template>
          </HttpSelectInputView>
          <div>
            {{ translate('modules.applications.conditions.description') }}
          </div>
          <div class="d-flex align-items-center g-4">
            <ButtonView type="normal" color="primary" class="w-100" @click="submit">
              {{ translate("save") }}
            </ButtonView>
            <ButtonView type="normal" color="secondary" class="w-100" @click="creating = false; editing = undefined">
              {{ translate("cancel") }}
            </ButtonView>
          </div>
          <SpinnerView :active="loading" color="primary"/>
        </div>
      </template>
    </ModalHttpFormView>
  </div>
</template>

<script setup>
import {InputView, TargetedModalHttpFormView, ModalHttpFormView, CollectionView, utilities, ButtonView, SpinnerView, HttpSelectInputView} from "@/niftybit";
import ResourcePermissionModalView from "@/systems/modules/roles/ResourcePermissionModalView.vue";
import {computed, ref} from "vue";
import authorization from "@/authorization";

defineProps({
  communityId: String
});

const canModify = computed(() => authorization.permission("modules.applications.categories.edit"));

const reload = ref(false);
const creating = ref(false);
const editing = ref(undefined);

</script>